import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  Organization,
  OrganizationContacts,
} from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { normalizeEmail } from '@/shared/lib';
import { Input, Modal, Select } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import {
  i18n as organizationI18n,
  organizationModel,
} from '@/entities/organization';

import {
  EditCompanyInfoFormPayload,
  editCompanyInfoFormSchema,
} from '../../config/formSchema';

import styles from './EditCompanyInfoModal.module.scss';

export type EditCompanyInfoModalProps = {
  organizationID: string;
  currentCountry: string;
  className?: string;
};

export const EditCompanyInfoModal: FC<EditCompanyInfoModalProps> = ({
  organizationID,
  className,
  currentCountry,
}) => {
  const dispatch = useAppDispatch();
  const { visible } = useAppSelector(
    (state) => state.modal.EditCompanyInfoModal,
  );

  const { formatMessage } = useIntl();

  const handleClose = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.EditCompanyInfoModal));
  }, []);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, isSubmitted },
  } = useForm<EditCompanyInfoFormPayload>({
    resolver: yupResolver(editCompanyInfoFormSchema),
    mode: 'all',
  });

  const organization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  // TODO change country options hook

  const getEditCompanyDefaultValues = (companyInfo: Organization) => ({
    companyName: companyInfo.Name,
    printName: companyInfo.PrintName,
    workEmail: companyInfo.Contacts?.Emails[0],
    phoneNumber: companyInfo.Contacts?.Phones[0],
    stateRegion: companyInfo.Contacts?.Region,
    city: companyInfo.Contacts?.City,
    zipCode: companyInfo.Contacts?.ZipCode,
    address: companyInfo.Contacts?.Address,
    webSite: companyInfo.Contacts?.WebSite,
  });

  const onSubmit: SubmitHandler<EditCompanyInfoFormPayload> = async (data) => {
    const {
      companyName,
      printName,
      phoneNumber,
      address,
      city,
      webSite,
      country,
      stateRegion,
      workEmail,
      zipCode,
    } = data;

    try {
      if (companyName) {
        await dispatch(
          organizationModel.thunks.setOrganizationName({
            OrganizationID: organizationID,
            Name: companyName,
          }),
        ).unwrap();
      }

      // A print name equal to the clinic name is stored empty, so report
      // headers keep following clinic renames until the user customises it
      const trimmedPrintName = printName?.trim() ?? '';
      const printNameToSave =
        trimmedPrintName === companyName?.trim() ? '' : trimmedPrintName;

      if (printNameToSave !== (organization?.PrintName ?? '')) {
        await dispatch(
          organizationModel.thunks.setOrganizationPrintName({
            OrganizationID: organizationID,
            PrintName: printNameToSave,
          }),
        ).unwrap();
      }

      const { Organization: updatedOrganization } = await dispatch(
        organizationModel.thunks.setOrganizationContacts({
          OrganizationID: organizationID,
          Contacts: {
            Address: address ?? '',
            City: city ?? '',
            Emails: workEmail ? [normalizeEmail(workEmail)] : [],
            Phones: phoneNumber ? [phoneNumber] : [],
            Country: country ? country.toString() : '',
            Region: stateRegion ?? '',
            WebSite: webSite ?? '',
            ZipCode: zipCode ?? '',
          } as OrganizationContacts,
        }),
      ).unwrap();

      if (updatedOrganization) {
        dispatch(organizationModel.actions.setOne(updatedOrganization));
      }
    } finally {
      handleClose();
    }
  };

  // Set default
  useEffect(() => {
    if (organization?.Contacts) {
      reset(getEditCompanyDefaultValues(organization));
    }

    return () => {
      reset(getEditCompanyDefaultValues({} as Organization));
    };
  }, [organization, reset, visible]);

  return (
    <Modal
      data-component="edit-company-info-modal"
      title={formatMessage({
        id: 'global.edit.clinic.info',
        defaultMessage: 'Edit clinic info',
      })}
      isOpen={visible}
      onCancel={handleClose}
      className={cn(className, styles.modal)}
      containerClassName={styles.modalContainer}
      borderless
      okButtonText={formatMessage({
        id: 'global.save',
        defaultMessage: 'Save',
      })}
      okButtonProps={{
        type: 'submit',
        form: 'editCompanyInfoForm',
        loading: isSubmitting,
      }}
    >
      <form id="editCompanyInfoForm" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="companyName"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Input
              required
              ref={ref}
              value={value}
              name={name}
              onBlur={() => {
                if (isSubmitted) {
                  onBlur();
                }
              }}
              onChange={(event) => {
                if (!isSubmitted) {
                  setValue(name, event.target.value);
                } else {
                  onChange(event);
                }
              }}
              type="text"
              autoComplete="organization"
              label={formatMessage({
                id: 'global.companyName',
                defaultMessage: 'Company name',
              })}
              error={error?.message}
            />
          )}
        />

        <div className={styles.row}>
          <Controller
            control={control}
            name="printName"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                type="text"
                placeholder={watch('companyName')}
                label={formatMessage(organizationI18n.printNameMessages.label)}
                error={error?.message}
              />
            )}
          />
        </div>

        <div className={styles.row}>
          <Controller
            control={control}
            name="workEmail"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error, isTouched },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={onBlur}
                onChange={(event) => {
                  if (!isTouched) {
                    setValue(name, event.target.value);
                  } else {
                    onChange(event);
                  }
                }}
                type="email"
                inputMode="email"
                autoComplete="email"
                label={formatMessage({
                  id: 'global.email',
                  defaultMessage: 'Email',
                })}
                error={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="phoneNumber"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                label={formatMessage({
                  id: 'editCompanyInfo.phone',
                  defaultMessage: 'Phone',
                })}
                error={error?.message}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="webSite"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Input
              ref={ref}
              value={value}
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              autoComplete="url"
              inputMode="url"
              label={formatMessage({
                id: 'editCompanyInfo.website',
                defaultMessage: 'Website',
              })}
              error={error?.message}
            />
          )}
        />

        <div className={styles.row}>
          <Controller
            control={control}
            name="country"
            render={({
              field: { ref, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Select
                className={styles.countrySelect}
                label={formatMessage({
                  id: 'global.country',
                  defaultMessage: 'Country',
                })}
                options={[
                  {
                    value: currentCountry,
                    label: currentCountry,
                  },
                ]}
                isDisabled
                ref={ref}
                value={currentCountry}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                error={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="stateRegion"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                inputMode="text"
                type="text"
                onBlur={onBlur}
                onChange={onChange}
                label={formatMessage({
                  id: 'global.stateRegion',
                  defaultMessage: 'State/Region',
                })}
                error={error?.message}
              />
            )}
          />
        </div>

        <div className={styles.row}>
          <Controller
            control={control}
            name="city"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                type="text"
                label={formatMessage({
                  id: 'editCompanyInfo.city',
                  defaultMessage: 'City',
                })}
                error={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="zipCode"
            render={({
              field: { ref, value, name, onBlur, onChange },
              fieldState: { error },
            }) => (
              <Input
                ref={ref}
                value={value}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                type="text"
                autoComplete="postal-code"
                label={formatMessage({
                  id: 'editCompanyInfo.zipCode',
                  defaultMessage: 'Zip code',
                })}
                error={error?.message}
              />
            )}
          />
        </div>

        <Controller
          control={control}
          name="address"
          render={({
            field: { ref, value, name, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Input
              ref={ref}
              value={value}
              name={name}
              inputMode="text"
              type="text"
              onBlur={onBlur}
              onChange={onChange}
              label={formatMessage({
                id: 'editCompanyInfo.address',
                defaultMessage: 'Address',
              })}
              error={error?.message}
            />
          )}
        />
      </form>
    </Modal>
  );
};
